import React, { FC, useEffect, useRef, useState, useLayoutEffect } from 'react';
import classes from '../asset/css/main.module.css';
import { Setting } from '../App';
import { Header } from '../components/subscription-detail/Header';
import { Summary } from '../components/subscription-detail/Summary';
import { RegisteredProductList } from '../components/subscription-detail/RegisteredProductList';
import { PausedProductList } from '../components/subscription-detail/PausedProductList';
import { Footer } from '../components/subscription-detail/Footer';
import { ChangeDeliveryDateModal } from '../components/change-delivery-date-modal/ChangeDeliveryDateModal';
import { SummaryType } from '../types/summary-type';
import { ProductInfoType } from '../types/product-info-type';
import { useSubscriptionDetail } from '../hooks/use-subscription-detail';
import { useUpdateDeliveryDate } from '../hooks/use-update-delivery-date';
import { DeliveryDateType } from '../types/delivery-date-type';
import { RecommendProductInfoType } from '../types/recommend-product-info-type';
import { ListSelectTab } from '../components/subscription-detail/ListSelectTab';
import { Menu } from '../components/subscription-detail/Menu';
import { useSubscriptionLine } from '../hooks/use-subscription-line';
import { useSubscriptionAddProducts } from '../hooks/use-subscription-add-products';
import { MessageModal } from '../components/message-modal/MessageModal';
import { MessageContentType } from '../types/message-content-type';

export const SubscriptionDetail: FC<{ setting: Setting }> = ({ setting }) => {
  const [subscriptionContractId, setSubscriptionContractId] = useState('');
  const [summary, setSummary] = useState<SummaryType>();
  const [registeredProducts, setRegisteredProducts] = useState<ProductInfoType[]>([]);
  const [pausedProducts, setPausedProducts] = useState<ProductInfoType[]>([]);
  const [recommendProducts, setRecommendProducts] = useState<RecommendProductInfoType[]>([]);
  const [loading, setLoading] = useState(false);

  const [changeDeliveryDateModalActive, setChangeDeliveryDateModalActive] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState<DeliveryDateType>({
    deliveryDate: new Date(),
    disableWeek: [false, false, false, false, false, false, false],
  });

  const [messageModalActive, setMessageModalActive] = useState(false);
  const [messageContent, setMessageContent] = useState<MessageContentType>();

  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setting.resize(root.current?.clientHeight ?? 0);
  }, [setting, root.current?.clientHeight]);

  // スクロール量を親windowから受け取る
  const [scrollOffset, setScrollOffset] = useState(0);
  useEffect(() => {
    const parentScrollEvent = (event: MessageEvent) => {
      const { pageYOffset } = event.data;
      if (!pageYOffset) return;

      setScrollOffset(pageYOffset);
    };
    window.addEventListener('message', parentScrollEvent);

    return () => window.removeEventListener('message', parentScrollEvent);
  }, []);

  useEffect(() => {
    window.parent.postMessage({ action: 'prepared' }, '*');
  }, []);

  const { addCartProducts } = useSubscriptionAddProducts(setting);

  const { getPausedProducts, getRegisteredProducts, getRecommendProducts } = useSubscriptionDetail(
    setting,
    setSubscriptionContractId,
    setRegisteredProducts,
    setSummary,
    setDeliveryDate,
    setPausedProducts,
    setRecommendProducts
  );

  const failed = (message: string) => {
    setMessageContent({
      title: '内部エラー',
      body: '毎月お届けプランの更新に失敗しました。',
    });
    setMessageModalActive(true);

    console.log(message);
    setLoading(false);
  };

  const addProductSuccess = () => {
    setMessageContent({
      title: '毎月お届けプランの更新',
      body: '商品を毎月お届けプランに追加しました。',
    });
    setMessageModalActive(true);

    getRegisteredProducts(
      (infos) => {
        // 登録済み商品一覧を更新する
        setRegisteredProducts(infos);

        // 休止中の商品一覧から追加した商品を削除する
        setPausedProducts(
          pausedProducts.reduce((result: ProductInfoType[], pausedProduct: ProductInfoType) => {
            // Mammababyは商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (infos.every((product) => product.variantId !== pausedProduct.variantId)) {
              result.push(pausedProduct);
            }

            return result;
          }, [])
        );

        // レコメンド商品一覧から追加した商品を削除する
        setRecommendProducts(
          recommendProducts.reduce((result: RecommendProductInfoType[], recommendProduct: RecommendProductInfoType) => {
            // Mammababyは商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (infos.every((product) => product.variantId !== recommendProduct.variantId)) {
              result.push(recommendProduct);
            }

            return result;
          }, [])
        );

        setLoading(false);
      },
      (message) => {
        console.log(message);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    const messageEvent = (e: MessageEvent) => {
      if (!e.data.cartItems) return;

      if (e.data.cartItems.length > 0) {
        addCartProducts(e.data.cartItems, addProductSuccess, failed);
      }
    };
    window.addEventListener('message', messageEvent);

    return () => window.removeEventListener('message', messageEvent);
  }, [addCartProducts, addProductSuccess]);

  const { updateDeliveryDate } = useUpdateDeliveryDate(setting);
  const updateDeliveryFunc = () => {
    updateDeliveryDate(
      subscriptionContractId,
      summary!.nextBillingDate,
      deliveryDate.deliveryDate,
      () => {
        setMessageContent({
          title: '毎月お届けプランの更新',
          body: '次回お届け日を更新しました。',
        });
        setMessageModalActive(true);

        setSummary({
          ...summary!,
          deliveryDate: deliveryDate.deliveryDate,
        });
        setChangeDeliveryDateModalActive(false);
      },
      failed
    );
  };

  const removeProductSuccess = (lineId: string) => {
    setMessageContent({
      title: '毎月お届けプランの更新',
      body: '商品を毎月お届けプランから削除しました。',
    });
    setMessageModalActive(true);

    // 削除した商品を登録済み商品一覧から削除する
    const products = registeredProducts.filter((product) => lineId !== product.lineId);
    setRegisteredProducts(products);
    // 休止中の商品一覧を更新する
    getPausedProducts(products, (infos) => {
      setPausedProducts(infos);
      setLoading(false);
    });
    // レコメンド商品一覧を更新する
    getRecommendProducts(products, (infos) => {
      setRecommendProducts(infos);
      setLoading(false);
    });
  };

  const updateQuantitySuccess = (lineId: string, quantity: number) => {
    setMessageContent({
      title: '毎月お届けプランの更新',
      body: '毎月お届けプランの商品の数量を更新しました。',
    });
    setMessageModalActive(true);

    // 数量を更新する
    const products = registeredProducts.map((product) => {
      if (lineId !== product.lineId) return product;

      return {
        ...product,
        quantity,
      };
    });

    setRegisteredProducts(products);

    // サマリの合計金額を更新する
    setSummary({
      ...summary!,
      totalPrice: summary!.deliveryPrice + products.reduce((sum, product) => sum + product.price * product.quantity, 0),
    });

    setLoading(false);
  };

  const { updateQuantity, removeProduct, addProduct } = useSubscriptionLine(setting);
  const updateQuantityFunc = (lineId: string, quantity: number) => {
    setLoading(true);

    if (quantity > 0) {
      updateQuantity(
        subscriptionContractId,
        lineId,
        quantity,
        () => {
          updateQuantitySuccess(lineId, quantity);
        },
        failed
      );
    } else {
      removeProduct(
        subscriptionContractId,
        lineId,
        () => {
          removeProductSuccess(lineId);
        },
        failed
      );
    }
  };

  const changeActiveFunc = (active: boolean, variantId: string, planId?: string, lineId?: string) => {
    setLoading(true);

    if (active) {
      removeProduct(
        subscriptionContractId,
        lineId!,
        () => {
          removeProductSuccess(lineId!);
        },
        failed
      );
    } else {
      addProduct(
        planId!,
        variantId,
        1,
        () => {
          addProductSuccess();
        },
        failed
      );
    }
  };

  const changeDeliveryDateFunc = (value: Date) => {
    setDeliveryDate({
      ...deliveryDate,
      deliveryDate: value,
    });
  };

  return (
    <>
      <div className={classes.mypage_subsc_container} ref={root}>
        <Header btnFunc={() => setChangeDeliveryDateModalActive(true)} />
        <Summary summary={summary} />
        <RegisteredProductList
          productInfos={registeredProducts}
          loading={loading}
          changeActiveFunc={changeActiveFunc}
          updateQuantityFunc={updateQuantityFunc}
        />
        <PausedProductList productInfos={pausedProducts} loading={loading} changeActiveFunc={changeActiveFunc} />
        <ListSelectTab
          recommendProductInfos={recommendProducts}
          loading={loading}
          changeActiveFunc={changeActiveFunc}
        />
        <Footer />
        <Menu />
      </div>
      <ChangeDeliveryDateModal
        active={changeDeliveryDateModalActive}
        scrollOffset={scrollOffset}
        deliveryDate={deliveryDate}
        changeDeliveryDateFunc={changeDeliveryDateFunc}
        updateDeliveryDateFunc={updateDeliveryFunc}
        closeFunc={() => setChangeDeliveryDateModalActive(false)}
      />
      <MessageModal
        active={messageModalActive}
        scrollOffset={scrollOffset}
        closeFunc={() => setMessageModalActive(false)}
        messageContent={messageContent}
      />
    </>
  );
};
